import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, ShieldCheck, PenLine, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function Letterhead({ brand, accent }) {
  return (
    <div className="flex flex-wrap justify-between items-start gap-4 pb-4 mb-4 border-b-2" style={{ borderColor: accent }}>
      <div className="flex items-center gap-3 min-w-0">
        {brand.logo_url ? <img src={brand.logo_url} alt="" className="h-14 w-14 object-contain" /> : <div className="h-14 w-14 rounded-xl flex items-center justify-center text-white font-display font-bold text-xl" style={{ background: accent }}>{(brand.company || "A")[0]}</div>}
        <div className="min-w-0">
          <p className="font-display font-extrabold text-xl leading-tight" style={{ color: accent }}>{brand.company || "Glamour Pools and Spas"}</p>
          {brand.tagline && <p className="text-xs text-slate-500">{brand.tagline}</p>}
          {brand.abn && <p className="text-[11px] text-slate-400">ABN {brand.abn}</p>}
        </div>
      </div>
      <div className="text-right text-[11px] text-slate-500 shrink-0">
        {brand.address && <p>{brand.address}</p>}
        {brand.phone && <p>{brand.phone}</p>}
        {brand.email && <p>{brand.email}</p>}
        {brand.website && <p>{brand.website}</p>}
      </div>
    </div>
  );
}

function DocumentBody({ data, accent }) {
  const { title, doc_type, number, status, date, client, sections, subtotal, discount, gst_total, total, notes } = data;
  return (
    <div className="p-6 sm:p-8 text-slate-800">
      <Letterhead brand={data.brand} accent={accent} />
      <div className="flex flex-wrap justify-between items-start gap-3 mb-5">
        <div>
          <p className="font-display font-bold text-lg text-slate-900">{title}</p>
          {number && <p className="font-mono2 text-sm text-slate-500">{doc_type === "quote" ? "Quote" : "Ref"} #{number}</p>}
          {status && <span className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize" style={{ background: `${accent}22`, color: accent }}>{status}</span>}
        </div>
        <div className="text-right text-sm">
          <p className="text-slate-400 text-[11px]">{date ? new Date(date).toLocaleDateString() : new Date().toLocaleDateString()}</p>
          {client && <div className="mt-1"><p className="font-semibold text-slate-800">{client.name}</p>{client.company && <p className="text-xs text-slate-500">{client.company}</p>}{client.phone && <p className="text-xs text-slate-500">{client.phone}</p>}{client.email && <p className="text-xs text-slate-500">{client.email}</p>}</div>}
        </div>
      </div>

      {(sections || []).map((sec, si) => (
        (sec.lines && sec.lines.length > 0) ? (
          <div key={si} className="mb-4">
            {sec.heading && <p className="font-mono2 text-[10px] uppercase tracking-widest mb-1" style={{ color: accent }}>{sec.heading}</p>}
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200"><th className="text-left py-1 font-medium">Description</th><th className="text-center py-1 font-medium w-14">Qty</th><th className="text-right py-1 font-medium w-20">Unit</th><th className="text-right py-1 font-medium w-24">Amount</th></tr></thead>
              <tbody>
                {sec.lines.map((ln, i) => (
                  <tr key={i} className="border-b border-slate-100"><td className="py-1.5">{ln.desc}</td><td className="text-center">{ln.qty}</td><td className="text-right font-mono2">{money(ln.unitPrice)}</td><td className="text-right font-mono2">{money(ln.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null
      ))}

      <div className="flex justify-end mt-4">
        <div className="w-60 text-sm space-y-1">
          <div className="flex justify-between text-slate-500"><span>Subtotal</span><span className="font-mono2">{money(subtotal)}</span></div>
          {discount > 0 && <div className="flex justify-between text-slate-500"><span>Discount</span><span className="font-mono2">-{money(discount)}</span></div>}
          <div className="flex justify-between font-display font-bold text-lg border-t border-slate-200 pt-1" style={{ color: accent }}><span>Total</span><span className="font-mono2">{money(total)}</span></div>
          <div className="flex justify-between text-[11px] text-slate-400"><span>Includes GST</span><span className="font-mono2">{money(gst_total)}</span></div>
        </div>
      </div>

      {notes && <div className="mt-5 text-sm text-slate-600 border-t border-slate-100 pt-3 whitespace-pre-line"><span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-400 block mb-1">Notes</span>{notes}</div>}
      <p className="text-center text-[11px] text-slate-400 mt-6">{data.footer_note}</p>
    </div>
  );
}

export default function AcceptDoc() {
  const { docType, docId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [decision, setDecision] = useState("accepted");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // acceptance object after submit

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/public/document/${docType}/${docId}`);
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "This document could not be found."); }
      setData(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [docType, docId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!name.trim()) { setError("Please type your full name to sign."); return; }
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/public/document/${docType}/${docId}/accept`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), decision, note: note.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Could not record your response.");
      setResult(d.acceptance);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="accept-loading"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>;

  if (error && !data) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6" data-testid="accept-not-found">
      <div className="max-w-md text-center bg-white rounded-2xl shadow-sm p-8">
        <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
        <p className="font-display font-bold text-slate-900 text-lg">Document unavailable</p>
        <p className="text-sm text-slate-500 mt-2">{error}</p>
      </div>
    </div>
  );

  const accent = (data.brand && data.brand.colour) || "#0891B2";
  const existing = result || data.acceptance;
  const isQuote = data.doc_type === "quote";

  return (
    <div className="min-h-screen bg-slate-100 py-6 sm:py-10 px-3" data-testid="accept-doc-page">
      <div className="mx-auto w-full max-w-2xl space-y-4">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <DocumentBody data={data} accent={accent} />
        </div>

        {existing ? (
          <div className={`rounded-2xl p-6 text-center shadow-sm ${existing.decision === "accepted" ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`} data-testid="accept-confirmed">
            {existing.decision === "accepted"
              ? <CheckCircle2 className="h-9 w-9 text-emerald-600 mx-auto mb-2" />
              : <XCircle className="h-9 w-9 text-rose-600 mx-auto mb-2" />}
            <p className="font-display font-bold text-slate-900 text-lg">{existing.decision === "accepted" ? "Accepted — thank you!" : "Response recorded"}</p>
            <p className="text-sm text-slate-600 mt-1">
              {existing.decision === "accepted" ? "Accepted" : "Declined"} by <span className="font-semibold">{existing.name}</span> on {new Date(existing.at).toLocaleString()}.
            </p>
            {existing.note && <p className="text-sm text-slate-500 mt-2 italic">“{existing.note}”</p>}
            <p className="text-[11px] text-slate-400 mt-3 inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Your name, response time and IP address are recorded as your electronic signature.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="accept-form">
            <p className="font-display font-bold text-slate-900">{isQuote ? "Do you approve this quote?" : "Confirm this booking?"}</p>
            <p className="text-sm text-slate-500 mt-1">Tap your choice, then type your name below to sign.</p>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <button onClick={() => setDecision("accepted")} data-testid="accept-choice-accept" className={`rounded-xl border-2 px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${decision === "accepted" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}><CheckCircle2 className="h-4 w-4" /> {isQuote ? "Approve" : "Confirm"}</button>
              <button onClick={() => setDecision("declined")} data-testid="accept-choice-decline" className={`rounded-xl border-2 px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${decision === "declined" ? "border-rose-500 bg-rose-50 text-rose-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}><XCircle className="h-4 w-4" /> Decline</button>
            </div>

            <div className="mt-4">
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 inline-flex items-center gap-1.5"><PenLine className="h-3.5 w-3.5" /> Type your full name to sign *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="accept-name-input" placeholder="Your full name" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" />
            </div>
            <div className="mt-3">
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400">Note (optional)</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="accept-note-input" placeholder="Anything you'd like us to know…" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" />
            </div>

            {error && <p className="text-sm text-rose-500 mt-3" data-testid="accept-error">{error}</p>}

            <button onClick={submit} disabled={busy} data-testid="accept-submit-btn" className="mt-4 w-full inline-flex items-center justify-center gap-2 text-white font-semibold px-6 py-3.5 rounded-full transition-opacity disabled:opacity-60" style={{ background: decision === "declined" ? "#e11d48" : accent }}>
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Recording…</> : decision === "declined" ? "Sign & decline" : isQuote ? "Sign & approve quote" : "Sign & confirm booking"}
            </button>
            <p className="text-center text-[11px] text-slate-400 mt-3 inline-flex items-center gap-1.5 w-full justify-center"><ShieldCheck className="h-3.5 w-3.5" /> Your name, response time and IP address are recorded as your electronic signature.</p>
          </div>
        )}
      </div>
    </div>
  );
}
